import React, { useContext, useEffect, useState } from "react"
import {
    BackHandler,
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { Portal } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { schoolAssessmentsService } from "@data/services/schoolAssessmentsService"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { DimensionsContext } from "@common/utils/mobileUtils"
import SchoolAssessmentTopicForm, { getSchoolAssessmentFormData } from "../../school-assessment/SchoolAssessmentTopicForm"

const AddSchoolAssessmentModal = ({
    chapters,
    chaptersLoading,
    loading,
    visible,
    onClose,
    onSuccess,
    assessment,
    studentId,
    schoolSubjects,
    currentUser,
    user,
    apiRequest,
    cameraOn = false,
}) => {
    const isEditing = !!assessment
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()
    const isLargeScreen = dimensions?.width > 800
    const targetUser = user || currentUser

    // Form state
    const [error, setError] = useState(null)
    const [formData, setFormData] = useState(getSchoolAssessmentFormData(assessment))
    // Media state
    const [isMediaMenuOpen, setIsMediaMenuOpen] = useState(false)

    // Android hardware back: close the modal instead of letting it pop the
    // underlying screen. The media menu installs its own handler that fires
    // first (last-registered wins).
    useEffect(() => {
        if (Platform.OS !== "android" || !visible || cameraOn) return
        const subscription = BackHandler.addEventListener(
            "hardwareBackPress",
            () => {
                if (isMediaMenuOpen) return false
                onClose()
                return true
            }
        )
        return () => subscription.remove()
    }, [visible, cameraOn, isMediaMenuOpen, onClose])

    // Handle API success/error
    // Extract detailed error message from Laravel validation errors
    function getErrorMessage(err) {
        // Laravel validation errors have { message: "...", errors: { field: ["msg1", "msg2"] } }
        if (err?.errors && typeof err.errors === "object") {
            const firstFieldErrors = Object.values(err.errors)[0]
            if (
                Array.isArray(firstFieldErrors) &&
                firstFieldErrors.length > 0
            ) {
                return firstFieldErrors[0]
            }
        }
        return err?.message || "Une erreur est survenue"
    }

    useApiStatus(
        schoolAssessmentsService.createSchoolAssessment,
        null,
        false,
        () => {
            setError(null)
            onSuccess()
        },
        (err) => {
            setError(getErrorMessage(err))
        }
    )

    useApiStatus(
        schoolAssessmentsService.updateSchoolAssessment,
        null,
        false,
        () => {
            setError(null)
            onSuccess()
        },
        (err) => {
            setError(getErrorMessage(err))
        }
    )
    function onSubmit() {
        setError(null)

        const payload = {
            student_id: studentId,
            school_subject_id: formData.selectedSubjectId,
            date: formData.date,
            chapter_ids: formData.selectedChapterIds,
            custom_chapters: formData.customChapters.length ? formData.customChapters : undefined,
            key_requirements: formData.keyRequirementsText.trim() || undefined,
        }

        if (isEditing) {
            apiRequest(
                schoolAssessmentsService.updateSchoolAssessment,
                { id: assessment.id },
                payload
            )
        } else {
            apiRequest(
                schoolAssessmentsService.createSchoolAssessment,
                {},
                payload
            )
        }
    }

    // Render the modal as a pure JS overlay via Portal — no React Native `<Modal>`.
    // RN's Modal (even when `transparent`) presents a native UIViewController on
    // iOS, which blocks `ImagePicker.launchImageLibraryAsync` from presenting
    // PHPicker (the gallery picker promise hangs silently). Using only View +
    // Portal keeps everything in the React/JS layer so the native picker can
    // present normally above the React Native root view controller.
    //
    // `cameraOn` only hides the overlay visually (display: none) so the form
    // state survives a camera trip — the underlying screen unmounts us when
    // `visible` flips to false.
    if (!visible) return null

    return (
        <Portal>
            <View
                style={[
                    StyleSheet.absoluteFill,
                    {
                        backgroundColor: theme.colors.blackTransparent2,
                        justifyContent: isLargeScreen ? "center" : "flex-end",
                        alignItems: "center",
                        zIndex: 1000,
                        elevation: 1000,
                        display: cameraOn ? "none" : "flex",
                    },
                ]}
            >
                <TouchableOpacity
                    style={StyleSheet.absoluteFill}
                    activeOpacity={1}
                    onPress={() => {
                        if (!isMediaMenuOpen) onClose()
                    }}
                />
                <KeyboardAvoidingView
                    // Android: SystemBarsFrame already ends the app at the keyboard.
                    behavior={Platform.OS === "ios" ? "padding" : undefined}
                    style={{
                        backgroundColor: theme.colors.white,
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        borderBottomLeftRadius: isLargeScreen ? 12 : 0,
                        borderBottomRightRadius: isLargeScreen ? 12 : 0,
                        width: isLargeScreen ? 500 : "100%",
                        height: isLargeScreen ? "90%" : undefined,
                        flex: isLargeScreen ? undefined : 1,
                        marginTop: isLargeScreen ? 0 : insets.top + 8,
                        overflow: "hidden",
                    }}
                >
                    <SchoolAssessmentTopicForm
                        formData={formData}
                        setFormData={setFormData}
                        isLoadingChapters={chaptersLoading}
                        chapters={chapters}
                        schoolSubjects={schoolSubjects}
                        studentId={studentId}
                        studentClass={targetUser?.profile?.class}
                        studentPedagogicalObjectives={
                            targetUser?.pedagogical_objectives
                        }
                        setIsMediaMenuOpen={setIsMediaMenuOpen}
                        isEditing={isEditing}
                        previousAssessment={assessment}
                        onSubmit={onSubmit}
                        loadingSubmit={loading}
                        onClose={onClose}
                        error={error}
                        setError={setError}
                    />
                </KeyboardAvoidingView>
            </View>
        </Portal>
    )
}

const mapStateToProps = (state) => {
    return {
        chapters: state.data.chapters?.chaptersList || [],
        chaptersLoading:
            state.api.getChaptersBySchoolSubjectId?.request || false,
        loading:
            state.api.createSchoolAssessment?.request ||
            state.api.updateSchoolAssessment?.request,
        currentUser: state.data.currentUser?.object,
        cameraOn: state.activity?.cameraOn || false,
    }
}

const mapDispatchToProps = {
    apiRequest,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddSchoolAssessmentModal)
